import {
  ADMIN_BLOGS,
  ADMIN_BLOGS_ADD,
  AdminGridItem,
  AdminHeader,
  AdminSpinner,
  BlogEntity,
  Button,
  Div,
  FormSeparator,
  Grid,
  H4,
  HeaderIconWrapper,
  INITIAL_PAGINATION_PAGE,
  PAGINATION_LIMIT,
  PlusIcon,
  SuccessToast,
  Tab,
  TabsWrapper,
  deleteBlogById,
  formatDate,
  getAllBlogs,
  handleError,
  handleShowPaginationButton,
  removeObjectWithId,
  useAuth,
} from '@vgrubs/components';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { BlogsDeleteModal } from './BlogsDelete';
import { BLOG_LIST_TABS, BlogListTab } from './data';

export const BlogsAdmin = () => {
  const navigate = useNavigate();
  const [blogs, setBlogs] = useState<BlogEntity[]>([]);
  const [loading, setLoading] = useState(false);
  const [deleteModal, setDeleteModal] = useState<number | null>(null);
  const [page, setPage] = useState(INITIAL_PAGINATION_PAGE);
  const [tab, setTab] = useState<BlogListTab>('all');

  const user = useAuth((state) => state.user);

  async function handleBlogDelete(id: number) {
    try {
      setLoading(true);
      await deleteBlogById(Number(id));
      SuccessToast('Blog deleted successfully');
    } catch (error) {
      handleError(error);
    } finally {
      setBlogs((prev) => removeObjectWithId(prev, id.toString()));
      setDeleteModal(null);
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!user) return;
    // A tab switched while a page was loading must not add that page to the
    // new tab's list.
    let current = true;

    const getBlogs = async () => {
      try {
        setLoading(true);
        const { data } = await getAllBlogs({
          page,
          limit: PAGINATION_LIMIT,
          type: tab === 'all' ? undefined : tab,
        });
        if (current) setBlogs((prev) => [...prev, ...data.data]);
      } catch (error) {
        if (current) handleError(error);
      } finally {
        if (current) setLoading(false);
      }
    };

    getBlogs();
    return () => {
      current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, tab]);

  const changeTab = (next: BlogListTab) => {
    if (next === tab) return;
    setTab(next);
    setPage(INITIAL_PAGINATION_PAGE);
    setBlogs([]);
  };

  const handlePagination = () => {
    setPage((prev) => prev + 1);
  };

  const showPaginationButton = handleShowPaginationButton(blogs.length, page);

  return (
    <>
      <AdminHeader>
        <HeaderIconWrapper
          onClick={() =>
            navigate(ADMIN_BLOGS_ADD, {
              state: { type: tab === 'caseStudy' ? 'caseStudy' : 'blog' },
            })
          }
        >
          <PlusIcon />
        </HeaderIconWrapper>
      </AdminHeader>
      {loading && <AdminSpinner isRootPage />}

      <TabsWrapper>
        {BLOG_LIST_TABS.map(({ tab: value, name }) => (
          <Tab
            key={value}
            selected={tab === value}
            onClick={() => changeTab(value)}
          >
            {name}
          </Tab>
        ))}
      </TabsWrapper>
      <Div $mtSM="20" />
      <Grid>
        {blogs.map(({ createdAt, title, imageUrl, id, type }, i) => (
          <AdminGridItem
            key={i}
            title={title}
            image={imageUrl}
            info={
              type === 'caseStudy'
                ? `Case study · ${formatDate(createdAt)}`
                : formatDate(createdAt)
            }
            onClickDelete={() => setDeleteModal(Number(id))}
            onClickEdit={() => navigate(`${ADMIN_BLOGS}/${id}`)}
          />
        ))}
      </Grid>

      <FormSeparator m="20px 0" />

      {!loading && !blogs.length && <H4 $center>No data to show</H4>}

      <Div $flex $justifyCenter $mt="20" $height="45px">
        {showPaginationButton && (
          <Button onClick={handlePagination}>Load More</Button>
        )}
      </Div>

      <BlogsDeleteModal
        setDeleteModal={setDeleteModal}
        deleteModal={deleteModal}
        handleBlogDelete={handleBlogDelete}
      />
    </>
  );
};
